import { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import config from "@src/config";
import { PlanFamily } from "@src/gql/graphql";
import {
	captureBoardPublishToggled,
	captureSharePanelLinkCopied,
} from "@src/utils/analytics";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getPublishedLink } from "../../board/use-whiteboard-navigation";
import { useMyPlanSuspense } from "../../plan/queries/fetch-plan";
import type { PermissionType } from "../permission-combobox";
import type { ShareablePublication } from "../shareable-entity";
import { useCopyTooltip } from "./use-copy-tooltip";
import { useShareWhiteboardMutations } from "./use-share-whiteboard-mutations";

export type ShareWhiteboardPanelStateArgs = {
	whiteboardId: string;
	publication?: ShareablePublication | null;
	intake?: {
		isOpen: boolean;
		key?: string | null;
	} | null;
};

export function useShareWhiteboardPanelState({
	whiteboardId,
	publication,
	intake,
}: ShareWhiteboardPanelStateArgs) {
	const boardDocumentId = useMemo(
		() => new WhiteboardDocumentId(whiteboardId),
		[whiteboardId],
	);
	const { mutations, isLoading: isShareWhiteboardMutationsLoading } =
		useShareWhiteboardMutations();
	const { data: plan } = useMyPlanSuspense();
	const triggerCopyTooltip = useCopyTooltip();

	const [isLinkCopiedTooltipOpen, setIsLinkCopiedTooltipOpen] = useState(false);
	const [isCollectLinkCopiedTooltipOpen, setIsCollectLinkCopiedTooltipOpen] =
		useState(false);
	const [activeTab, setActiveTab] = useState<string | null>("share");

	const isCollectNotesEnabled = intake?.isOpen ?? false;
	const [localPublishedKey, setLocalPublishedKey] = useState<
		string | null | undefined
	>(undefined);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset local state on prop change
	useEffect(() => {
		setLocalPublishedKey(undefined);
	}, [whiteboardId, publication?.key]);

	const currentPublishedKey =
		localPublishedKey !== undefined ? localPublishedKey : publication?.key;

	const isPublished = useMemo(
		() => currentPublishedKey != null,
		[currentPublishedKey],
	);

	const [allowTemplate, setAllowTemplate] = useState(
		publication?.allowClone ?? false,
	);

	const isStarterPlan = plan.planFamily === PlanFamily.Starter;
	const [permission, setPermission] = useState<PermissionType>(
		isStarterPlan || (publication?.isReadonly ?? false)
			? "can view"
			: "can edit",
	);

	const handleCopyPublishLinkTooltip = useCallback(() => {
		captureSharePanelLinkCopied(boardDocumentId, "publish");
		triggerCopyTooltip(setIsLinkCopiedTooltipOpen);
	}, [boardDocumentId, triggerCopyTooltip]);

	const handleCopyCollectLinkTooltip = useCallback(() => {
		captureSharePanelLinkCopied(boardDocumentId, "collect_notes");
		triggerCopyTooltip(setIsCollectLinkCopiedTooltipOpen);
	}, [boardDocumentId, triggerCopyTooltip]);

	const handleShareWhiteboard = useCallback(
		async (allowClone: boolean) => {
			try {
				await mutations.publishWhiteboard({
					whiteboardId,
					isReadonly: permission === "can view",
					allowClone,
				});
				showSuccessNotification("Success", "Whiteboard published successfully");
			} catch (error) {
				handleError(error as Error, "Failed to publish whiteboard");
			}
		},
		[whiteboardId, mutations.publishWhiteboard, permission],
	);

	const handlePublishAndCollect = useCallback(async () => {
		try {
			await mutations.publishWhiteboard({
				whiteboardId,
				isReadonly: permission === "can view",
				allowClone: allowTemplate,
			});
			await mutations.openIntake(whiteboardId);
			showSuccessNotification(
				"Success",
				"Whiteboard published and note collection enabled",
			);
		} catch (error) {
			handleError(error as Error, "Failed to publish and enable collection");
		}
	}, [
		whiteboardId,
		mutations.publishWhiteboard,
		mutations.openIntake,
		permission,
		allowTemplate,
	]);

	const handleUnshareWhiteboard = useCallback(async () => {
		try {
			await mutations.unpublishWhiteboard(whiteboardId);
			showSuccessNotification("Success", "Whiteboard unpublished successfully");
			setAllowTemplate(false);
		} catch (error) {
			handleError(error as Error, "Failed to unpublish whiteboard");
		}
	}, [whiteboardId, mutations.unpublishWhiteboard]);

	const handleAllowClone = useCallback(async () => {
		try {
			await mutations.allowCloneWhiteboard(whiteboardId);
			showSuccessNotification(
				"Success",
				"Whiteboard allowed to be template successfully",
			);
		} catch (error) {
			handleError(error as Error, "Failed to allow template whiteboard");
			setAllowTemplate(false);
		}
	}, [whiteboardId, mutations.allowCloneWhiteboard]);

	const handleDenyClone = useCallback(async () => {
		try {
			await mutations.denyCloneWhiteboard(whiteboardId);
			showSuccessNotification(
				"Success",
				"Whiteboard denied to be template successfully",
			);
		} catch (error) {
			handleError(error as Error, "Failed to deny template whiteboard");
			setAllowTemplate(true);
		}
	}, [whiteboardId, mutations.denyCloneWhiteboard]);

	const handleAllowAnonymousEdit = useCallback(async () => {
		try {
			await mutations.allowAnonymousEdit(whiteboardId);
			showSuccessNotification(
				"Success",
				"Whiteboard allowed to be edited by anonymous users successfully",
			);
		} catch (error) {
			handleError(error as Error, "Failed to allow anonymous edit");
			setPermission("can view");
		}
	}, [whiteboardId, mutations.allowAnonymousEdit]);

	const handleDenyAnonymousEdit = useCallback(async () => {
		try {
			await mutations.denyAnonymousEdit(whiteboardId);
			showSuccessNotification(
				"Success",
				"Whiteboard denied to be edited by anonymous users successfully",
			);
		} catch (error) {
			handleError(error as Error, "Failed to deny anonymous edit");
			setPermission("can edit");
		}
	}, [whiteboardId, mutations.denyAnonymousEdit]);

	const handleTemplateChange = useCallback(
		(isChecked: boolean) => {
			setAllowTemplate(isChecked);
			if (isPublished) {
				if (isChecked) {
					handleAllowClone();
				} else {
					handleDenyClone();
				}
			}
		},
		[isPublished, handleAllowClone, handleDenyClone],
	);

	const handleSwitchChange = useCallback(
		async (checked: boolean) => {
			captureBoardPublishToggled(boardDocumentId, checked);
			if (checked) {
				handleShareWhiteboard(allowTemplate);
			} else {
				handleUnshareWhiteboard();
			}
		},
		[
			handleShareWhiteboard,
			handleUnshareWhiteboard,
			allowTemplate,
			boardDocumentId,
		],
	);

	const handleCollectNotesSwitchChange = useCallback(
		async (checked: boolean) => {
			try {
				if (checked) {
					await mutations.openIntake(whiteboardId);
					showSuccessNotification("Success", "Note collection is now open");
				} else {
					await mutations.closeIntake(whiteboardId);
					showSuccessNotification("Success", "Note collection has been closed");
				}
			} catch (error) {
				handleError(error as Error, "Failed to update note collection");
			}
		},
		[whiteboardId, mutations.openIntake, mutations.closeIntake],
	);

	const handlePermissionChange = useCallback(
		(value: PermissionType) => {
			if (isPublished) {
				if (value !== permission) {
					if (value === "can edit") {
						handleAllowAnonymousEdit();
					} else {
						handleDenyAnonymousEdit();
					}
				}
			}
			setPermission(value);
		},
		[
			handleAllowAnonymousEdit,
			handleDenyAnonymousEdit,
			isPublished,
			permission,
		],
	);

	const clipboardPublishedLink = useMemo(
		() =>
			currentPublishedKey
				? getPublishedLink(whiteboardId, currentPublishedKey)
				: null,
		[whiteboardId, currentPublishedKey],
	);

	const collectNotesLink = useMemo(() => {
		if (!intake?.key) return null;
		return `${config.collectNotesUrl}/${whiteboardId}`;
	}, [intake?.key, whiteboardId]);

	return {
		boardDocumentId,
		activeTab,
		setActiveTab,
		isLinkCopiedTooltipOpen,
		isCollectLinkCopiedTooltipOpen,
		isCollectNotesEnabled,
		isPublished,
		allowTemplate,
		permission,
		isLoading: isShareWhiteboardMutationsLoading,
		handleCopyPublishLinkTooltip,
		handleCopyCollectLinkTooltip,
		handleSwitchChange,
		handleTemplateChange,
		handlePermissionChange,
		handlePublishAndCollect,
		handleCollectNotesSwitchChange,
		clipboardPublishedLink,
		collectNotesLink,
	};
}
